//@ flow
import {
  all,
  call,
  take,
  takeLatest,
  put,
  select,
  actionChannel,
} from "redux-saga/effects";
import * as Sentry from "@sentry/browser";
import { delay } from "redux-saga";
import {
  adicionarMedidas,
  adicionarMedidasParaPersonalizar,
  atualizarMedidasUsuario,
  defineChavesI18n,
  setErroInicializacao,
  getMedidasFixas,
  iniciarApp,
  carregarDominioSuccess,
  carregarMedidasIniciaisSuccess,
  importarMedidaUsuario,
  selecionarAtivos,
  abrirArquivo,
  obterDetalhesUsuarioSuccess,
  getIdiomaUsuario,
  selecionarDataBase,
  getDataBaseDefault,
  getUltimos4MesesCompostosComDataBaseDefault,
  selecionarFechamentoDeMes,
  abrirTemplate,
  setCurrentUser,
  getModoApp,
  getConfiguracoesRelatorioSalvo,
  getMedidasSelecionadas,
  restaurarConfiguracoesRelatorio,
} from "../state";
import {
  type ArquivoMedida,
  CHAVES_ULTIMOS_QUATRO_MESES,
  FORMATO_DATA_BANCO,
  TIPO_DATA_BASE_FECHAMENTO,
  MODO_APP_SERIES,
} from "../model";
import moment from "moment";
import { assoc, prop, zipObj, lensProp, find, propEq } from "ramda";
import { overIf } from "../util";

function* contarPageView({ frameApi, contarPageViewConfiguracao }) {
  if (contarPageViewConfiguracao) {
    const modoApp = yield select(getModoApp);
    modoApp === MODO_APP_SERIES
      ? yield frameApi.pageview("SERIES_DE_DADOS_NOVO__CONFIGURACOES")
      : yield frameApi.pageview("RELATORIO_DE_ATIVOS");
  }
}

export function* carregarDominio({ api }) {
  const modoApp = yield select(getModoApp);
  const dominio = yield api.carregarDominio(
    modoApp === "SERIES" ? "SERIES" : "NORMAL"
  );
  yield put(
    carregarDominioSuccess({
      ...dominio,

      // TODO: vir da API
      funcoesRanking: [
        {
          id: 1,
          funcao: "ranking",
          chaveTraducao: "configuracaoRelatorio.ranking.colunas.ranking",
        },
        {
          id: 2,
          funcao: "percentil",
          chaveTraducao: "configuracaoRelatorio.ranking.colunas.percentil",
        },
        {
          id: 3,
          funcao: "indice",
          chaveTraducao: "configuracaoRelatorio.ranking.colunas.indice",
        },
      ],
    })
  );
}

export function* carregarDetalhesUsuario({ api, idioma }) {
  const detalhesUsuario = yield api.obterDetalhesUsuario();
  const usuario = yield api.currentUser();
  yield put(setCurrentUser(usuario));
  yield put(obterDetalhesUsuarioSuccess({ ...detalhesUsuario, idioma }));
}

export function* carregaChavesI18n({ api }) {
  yield all([take("OBTER_DETALHES_USUARIO_SUCCESS"), take("CARREGAR_DOMINIO")]);

  const modoApp = yield select(getModoApp);

  const idiomaUsuario: string = yield select(getIdiomaUsuario);
  const chavesI18n: string = yield api.listI18nKeys(
    idiomaUsuario,
    modoApp === "SERIES" ? "SERIES_DE_ATIVOS" : "RELATORIO_DE_ATIVOS"
  );

  const ultimos4MesesCompostos = yield select(
    getUltimos4MesesCompostosComDataBaseDefault
  );
  const traducoesUltimosQuatroMeses = zipObj(
    CHAVES_ULTIMOS_QUATRO_MESES,
    ultimos4MesesCompostos.map(prop("nomeTraduzido"))
  );

  const traducoesResultado = {
    "ag-grid.andCondition": "E",
    "ag-grid.orCondition": "OU",
    "ag-grid.copy": "Copiar",
    "ag-grid.copyWithHeaders": "Copiar com os cabeçalhos",
    "ag-grid.rowGroupColumnsEmptyMessage":
      "Arraste as colunas nessa área para agrupar",
    "ag-grid.group": "Grupo",
    "ag-grid.groups": "Grupos",
    "ag-grid.groupBy": "Agrupar por",
    "ag-grid.valueAggregation": "Agregações",
    "ag-grid.totalAndFilteredRows": "Linhas",
    "ag-grid.selectedRows": "Selecionadas",
    "ag-grid.average": "Média",
    "ag-grid.count": "Quantidade",
    "ag-grid.median": "Mediana",
    "ag-grid.min": "Mínimo",
    "ag-grid.max": "Máximo",
    "ag-grid.avg": "Média",
    "ag-grid.sum": "Soma",
    "ag-grid.maxDate": "Data mais recente",
    "ag-grid.minDate": "Data mais antiga",
    "ag-grid.page": "Página",
    "ag-grid.to": "até",
    "ag-grid.of": "de",
    "ag-grid.first": "Primeiro",
    "ag-grid.previous": "Anterior",
    "ag-grid.next": "Próximo",
    "ag-grid.last": "Último",
    "ag-grid.pinColumn": "Fixar coluna",
    "ag-grid.pinLeft": "Fixar à esquerda",
    "ag-grid.pinRight": "Fixar à direita",
    "ag-grid.noPin": "Não fixar",
    "ag-grid.autosizeThiscolumn": "Redimensionar automaticamente essa coluna",
    "ag-grid.autosizeAllColumns":
      "Redimensionar automaticamente todas as colunas",
    "ag-grid.resetColumns": "Resetar colunas",
    "ag-grid.equals": "Igual",
    "ag-grid.greaterThan": "Maior",
    "ag-grid.greaterThanOrEqual": "Maior ou igual",
    "ag-grid.lessThan": "Menor",
    "ag-grid.lessThanOrEqual": "Maior ou igual",
    "ag-grid.notEqual": "Diferente",
    "ag-grid.inRange": "Dentro da faixa",
    "ag-grid.startsWith": "Começa com",
    "ag-grid.endsWith": "Termina com",
    "ag-grid.contains": "Contém",
    "ag-grid.notContains": "Não contém",
    "ag-grid.clearFilter": "Limpar filtro",
    "ag-grid.applyFilter": "Aplicar filtro",
    "ag-grid.filterOoo": "Filtros...",

    // Chart Menu
    "ag-grid.chartRange": "Gerar Gráfico",
    "ag-grid.columnChart": "Coluna",
    "ag-grid.groupedColumn": "Agrupado",
    "ag-grid.stackedColumn": "Empilhado",
    "ag-grid.normalizedColumn": "Empilhado Percentual",
    "ag-grid.barChart": "Barra",
    "ag-grid.groupedBar": "Agrupado",
    "ag-grid.stackedBar": "Empilhado",
    "ag-grid.normalizedBar": "Empilhado Percentual",
    "ag-grid.pieChart": "Setores",
    "ag-grid.pie": "Pizza",
    "ag-grid.doughnut": "Donut",
    "ag-grid.line": "Linha",
    "ag-grid.xyChart": "Dispersão",
    "ag-grid.scatter": "Pontos",
    "ag-grid.bubble": "Bolha",
    "ag-grid.areaChart": "Área",
    "ag-grid.area": "Área",
    "ag-grid.stackedArea": "Empilhado",
    "ag-grid.normalizedArea": "Empilhado Percentual",
    "ag-grid.pivotChartAndPivotMode": "Gráfico Pivot & Modo Pivot",
    "ag-grid.pivotChart": "Gráfico Pivot",

    // Chart Options
    "ag-grid.pivotChartTitle": "Pivot Chart",
    "ag-grid.rangeChartTitle": "Gráfico da Seleção",
    "ag-grid.settings": "Configurações",
    "ag-grid.data": "Dados",
    "ag-grid.format": "Formato",
    "ag-grid.categories": "Categorias",
    "ag-grid.series": "Séries",
    "ag-grid.xyValues": "Valores X Y (Dispersão)",
    "ag-grid.paired": "Modo Pareado",
    "ag-grid.axis": "Eixos",
    "ag-grid.color": "Cor",
    "ag-grid.thickness": "Espessura",
    "ag-grid.xRotation": "Rotação X",
    "ag-grid.yRotation": "Rotação Y",
    "ag-grid.ticks": "Traços",
    "ag-grid.width": "Largura",
    "ag-grid.length": "Comprimento",
    "ag-grid.padding": "Espaçamento",
    "ag-grid.chart": "Gráfico",
    "ag-grid.title": "Título",
    "ag-grid.background": "Plano de fundo",
    "ag-grid.enabled": "Habilitado",
    "ag-grid.font": "Fonte",
    "ag-grid.top": "Cima",
    "ag-grid.right": "Direita",
    "ag-grid.bottom": "Baixo",
    "ag-grid.left": "Esquerda",
    "ag-grid.labels": "Identificadores",
    "ag-grid.size": "Tamanho",
    "ag-grid.minSize": "Tamanho Mínimo",
    "ag-grid.maxSize": "Tamanho Máximo",
    "ag-grid.legend": "Legenda",
    "ag-grid.position": "Posição",
    "ag-grid.markerSize": "Tamanho do Marcador",
    "ag-grid.markerStroke": "Traço do Marcador",
    "ag-grid.markerPadding": "Espaçamento do Marcador",
    "ag-grid.itemPaddingX": "Espaçamento em X",
    "ag-grid.itemPaddingY": "Espaçamento em Y",
    "ag-grid.strokeWidth": "Largura do Traço",
    "ag-grid.offset": "Posicionamento",
    "ag-grid.offsets": "Posicionamentos",
    "ag-grid.tooltips": "Tooltips",
    "ag-grid.callout": "Explicações",
    "ag-grid.markers": "Marcadores",
    "ag-grid.shadow": "Sombra",
    "ag-grid.blur": "Blur",
    "ag-grid.xOffset": "Posição X",
    "ag-grid.yOffset": "Posição Y",
    "ag-grid.lineWidth": "Largura da Linha",
    "ag-grid.normal": "Normal",
    "ag-grid.bold": "Negrito",
    "ag-grid.italic": "Itálico",
    "ag-grid.boldItalic": "Negrito e Itálico",
    "ag-grid.predefined": "Predefinido",
    "ag-grid.fillOpacity": "Opacidade do Preenchimento",
    "ag-grid.strokeOpacity": "Opacidade da Linha",
    "ag-grid.columnGroup": "Coluna",
    "ag-grid.barGroup": "Barra",
    "ag-grid.pieGroup": "Setores",
    "ag-grid.lineGroup": "Linha",
    "ag-grid.scatterGroup": "Dispersão",
    "ag-grid.areaGroup": "Área",
    "ag-grid.groupedColumnTooltip": "Agrupado",
    "ag-grid.stackedColumnTooltip": "Empilhado",
    "ag-grid.normalizedColumnTooltip": "Empilhado Percentual",
    "ag-grid.groupedBarTooltip": "Agrupado",
    "ag-grid.stackedBarTooltip": "Empilhado",
    "ag-grid.normalizedBarTooltip": "Empilhado Percentual",
    "ag-grid.pieTooltip": "Pizza",
    "ag-grid.doughnutTooltip": "Donut",
    "ag-grid.lineTooltip": "Linha",
    "ag-grid.groupedAreaTooltip": "Agrupado",
    "ag-grid.stackedAreaTooltip": "Empilhado",
    "ag-grid.normalizedAreaTooltip": "Empilhado Percentual",
    "ag-grid.scatterTooltip": "Dispersão",
    "ag-grid.bubbleTooltip": "Bolha",
    "ag-grid.noDataToChart": "Sem dados disponíveis para gerar gráfico.",
    "ag-grid.pivotChartRequiresPivotMode":
      "Gráfico Pivot necessita que o Modo Pivot esteja habilitado.",
    "ag-grid.noRowsToShow": "Sem linhas para exibir",
  };

  yield put(
    defineChavesI18n({
      ...traducoesResultado,
      ...chavesI18n,
      ...traducoesUltimosQuatroMeses,
    })
  );
}

function* obterAtivosSelecionados({ frameApi }) {
  yield take("CARREGAR_MEDIDAS_INICIAIS_SUCCESS");

  const ativosSelecionados = yield frameApi.fetchUserSelection();
  yield put(selecionarAtivos(ativosSelecionados));
}

function* carregarMedidasIniciais({ frameApi, restaurarCache }) {
  const configsRestoredChannel = yield actionChannel(
    "CONFIGURACOES_RESTAURADAS"
  );

  yield take("CARREGAR_DOMINIO");

  const tipoDataBase = frameApi.obterTipoDataBase();
  if (tipoDataBase === TIPO_DATA_BASE_FECHAMENTO && restaurarCache) {
    yield put(selecionarFechamentoDeMes(true));
  }
  const dataBase =
    frameApi.obterDataBase() || (yield select(getDataBaseDefault));

  const arquivoEmCache: ?ArquivoMedida = frameApi.obterArquivoMedidas();
  if (arquivoEmCache != null) {
    yield put(abrirArquivo(arquivoEmCache, { sobrescreverMedidas: false }));
  }

  const medidasSelecionadasEmCache: Array<MedidaSelecionada> = frameApi.obterMedidasSelecionadas();
  if (
    medidasSelecionadasEmCache != null &&
    medidasSelecionadasEmCache.length > 0 &&
    restaurarCache
  ) {
    const medidasSelecionadasEmCacheDataBase = medidasSelecionadasEmCache
      .map(medida =>
        "dataBase" in medida ? assoc("dataBase", dataBase, medida) : medida
      )
      .map(medida =>
        "dataBaseDeflatorDataEspecifica" in medida
          ? overIf(lensProp("dataBaseDeflatorDataEspecifica"), moment)(medida)
          : medida
      );
    yield put(adicionarMedidas(medidasSelecionadasEmCacheDataBase, true));

    const medidasParaPersonalizar = frameApi.obterMedidasParaPersonalizar();
    if (medidasParaPersonalizar != null && medidasParaPersonalizar.length > 0) {
      yield put(adicionarMedidasParaPersonalizar(medidasParaPersonalizar));
    }
  } else if (restaurarCache) {
    const medidasIniciais: Array<MedidaSelecionada> = yield select(
      getMedidasFixas
    );
    yield put(adicionarMedidas(medidasIniciais));
  }

  if (restaurarCache) {
    yield delay(1000);
    yield put(selecionarDataBase(moment(dataBase, FORMATO_DATA_BANCO)));
  } else {
    yield take(configsRestoredChannel);
  }

  yield put(carregarMedidasIniciaisSuccess());
}

export default function* bootSaga({
  api,
  frameApi,
  history,
  contarPageViewConfiguracao,
}) {
  yield takeLatest("CARREGAR_APP", function*(action) {
    try {
      yield all([
        call(obterAtivosSelecionados, { frameApi }),
        call(carregarMedidasIniciais, {
          frameApi,
          restaurarCache: action.restaurarCache,
        }),
        call(carregarDominio, { api }),
        call(carregarDetalhesUsuario, {
          api,
          idioma: yield frameApi
            .fetchCommonHeaders()
            .then(prop("Accept-Language")),
        }),
        call(contarPageView, {
          frameApi,
          contarPageViewConfiguracao: action.contarPageViewConfiguracao,
        }),
        call(carregaChavesI18n, { api }),
      ]);

      yield put(atualizarMedidasUsuario());

      if (
        action.idMedidaUsuario &&
        parseInt(action.idMedidaUsuario) &&
        !(yield select(getMedidasSelecionadas)).some(
          medida => medida.idMedidaUsuario === parseInt(action.idMedidaUsuario)
        )
      ) {
        yield put(importarMedidaUsuario(action.idMedidaUsuario));
      }

      if (action.abrirTemplate) {
        const template = yield api
          .obterListaTemplatesMedidas()
          .then(find(propEq("nome", action.abrirTemplate)));
        yield put(abrirTemplate(template));
        yield take([
          "REPORT_FETCHED",
          "ERRO_AO_ABRIR_ARQUIVO",
          "REPORT_FETCHING_ERROR",
        ]);
        yield put(iniciarApp());
      } else {
        const modoApp = yield select(getModoApp);
        if (modoApp === MODO_APP_SERIES) {
          // Ao restaurar as configurações de um relatório que já foi calculado, quando o usuárioe entra e sai da tela por exemplo,
          // Existem várias sagas que chamam o adicionarMedidas e toda vez que as medidas são adicionadas o edicaoMedida é resetado
          // Como no modo séries existem algumas coisas que ficam no edicaoMedida mas são configurações globais (periodo, dataInicial, dataFinal), esses campos eram resetados pro default
          // Dessa forma eu tive que fazer a restauração das configurações logo antes de flagar o app como iniciado, para evitar esses side effects de outras sagas
          const configuracoesRelatorioSalvo = yield select(
            getConfiguracoesRelatorioSalvo
          );
          if (configuracoesRelatorioSalvo) {
            const medidas = yield select(getMedidasSelecionadas);
            yield put(
              restaurarConfiguracoesRelatorio(
                medidas,
                configuracoesRelatorioSalvo
              )
            );
          }
        }
        yield put(iniciarApp());
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error(error);
      } else if (process.env.NODE_ENV === "production") {
        Sentry.captureException(error);
      }

      yield put(setErroInicializacao(error));
    }
  });
}

// const carregar = function*() {
//   if (action.template) {
//     yield abrirTemplate(action.template);
//     yield openSelecaoMedidas();
//   } else {
//     if (action.medidaUsuario) {
//       yield carregarMedidaUsuario(action.medidaUsuario);
//     } else {
//       if (action.resultadoSalvo) {
//         yield carregarResultado(action.resultadoSalvo);
//         // Sincronizar ativos com seleçào
//         if (voltarPara == "/resultado") {
//           yield selecionarMedidas(action.resultadoSalvo);
//           return yield openResultado();
//         }
//       }
//
//       const medidasEditadas = yield getMedidasEditadas();
//       if (medidasEditadas) {
//         yield selecionarMedidas(medidasEditadas);
//       }
//
//       yield openSelecaoMedidas();
//     }
//   }
// }
